import GoogleMap, { ChangeEventValue, Coords, MapOptions } from 'google-map-react';
import { ComponentProps, ReactNode } from 'react';

import { MapMarker } from 'dashboard/components/Map/MapMarker';
import Marker from 'dashboard/components/Map/Marker';
import Card from 'design-system/components/Card/Card';
import { Place } from 'lib/interfaces/place';

export interface MapProps {
  /**
   * Array of markers to display. Can possibly enhance to ensure each has a
   * UID, so we can map click handlers to each. We can always fall back to
   * array index if need be.
   *
   * We probably DON'T want to pass anything less primitive than
   * `LatLng` such as addresses (+ calls to `google.maps.geocodeByAddress()`).
   * That should be the parent's responsibility for maximum flexibility.
   */
  markers: MapMarker[];
  /**
   * Will mostly be used to recenter the map on click and maybe zoom.
   */
  onMarkerClick?: (markerIdx: number, markerProps: ComponentProps<typeof Marker>) => void;
  /**
   * Sets the current center. Is more of an imperative update than a controlled
   * value. For example, the user can zoom out with the map controls even if
   * this value is set.
   */
  center?: Coords;
  /**
   * Determines coordinates on mount. Should be treated as immutable.
   */
  zoom?: number;
  onChange?: ({ bounds, center, marginBounds, size, zoom }: ChangeEventValue) => void;
  renderTooltip?: (marker: MapMarker) => ReactNode;
  isMarkerActive?: (place: Place) => boolean;
  isApproximate?: boolean;
  /**
   * Configuration options through google-map-react
   */
  mapOptions?: MapOptions;
  /**
   * For Higher Order Map Components to set up any Google Map Internal API needs
   */
  handleGoogleApiLoaded?: (map: any, maps: any) => void;
  /**
   * Reference to the actual Google Map canvas (Set through the Internal API loader)
   * No need to use if just using this standalone Map component
   */
  map?: google.maps.Map;
}

/**
 * We use coordinates for San Francisco as our default.
 */
export const DEFAULT_CENTER = {
  lat: 37.7749,
  lng: -122.4194,
};
export const DEFAULT_ZOOM = 5;
// The furthest zoom we allow on our map; built into the library
export const MIN_ZOOM = 2;

/**
 * Caveats:
 * This component MUST be inside a container with `height` EXPLICITLY defined.
 * Else, it will not display.
 *
 * Note that we do NOT need to pass API keys because we always load it in the
 * document head.
 */
const Map = ({
  markers,
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  onMarkerClick,
  onChange,
  renderTooltip,
  isMarkerActive,
  isApproximate,
  map,
  handleGoogleApiLoaded,
  mapOptions,
}: MapProps) => {
  // Because we load Google Maps via script tag, it can potentially be blocked,
  // fail to load, etc. In this case, render nothing instead of crashing the
  // page.
  if (window.google?.maps == null) {
    return (
      <Card>
        <Card.Body>
          <Card.Subtitle>Map failed to load</Card.Subtitle>
          Please try reloading the page and ensure you are not blocking external scripts.{' '}
        </Card.Body>
      </Card>
    );
  }

  /**
   * TODO: add effect that runs when markers change, and update bounding box to
   * show all markers. Need to tweak to get best UX.
   */

  return (
    <GoogleMap
      center={center}
      zoom={zoom}
      onChange={onChange}
      onChildClick={onMarkerClick}
      yesIWantToUseGoogleMapApiInternals={true}
      onGoogleApiLoaded={({ map, maps }) =>
        handleGoogleApiLoaded && handleGoogleApiLoaded(map, maps)
      }
      options={mapOptions}
    >
      {markers.map(
        (
          {
            lat,
            lng,
            radiusMeters,
            hasDot,
            styleType,
            primaryTooltipLabel,
            secondaryTooltipLabel,
            focusedMarkerColor,
            defaultMarkerColor,
            overlapCount,
          },
          i,
        ) => (
          <Marker
            key={i}
            lat={lat}
            lng={lng}
            radiusMeters={radiusMeters}
            styleType={styleType}
            primaryTooltipLabel={primaryTooltipLabel}
            secondaryTooltipLabel={secondaryTooltipLabel}
            hasDot={hasDot}
            focusedMarkerColor={focusedMarkerColor}
            defaultMarkerColor={defaultMarkerColor}
            map={map}
            isActive={isMarkerActive != null ? isMarkerActive({ lat, lng, radiusMeters }) : false}
            renderTooltip={renderTooltip}
            isApproximate={isApproximate}
            overlapCount={overlapCount}
          />
        ),
      )}
    </GoogleMap>
  );
};

export default Map;
